import { createSelector } from '../reselect';
import { getNewStatsService, total, simpleChart, safeDivide, simpleChart2 } from '../generalUtils';
import moment from '@tw/moment-cached/module/timezone';

// spend
const totalGoogleAdsCost = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'spend');
});

const chartGoogleAds = createSelector([getNewStatsService('google-ads')], (stats) => {
  return simpleChart(stats, 'spend');
});

// clicks
const totalGoogleAdsClicks = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'clicks');
});

const googleAdsClicksChart = createSelector([getNewStatsService('google-ads')], (stats) => {
  return simpleChart(stats, 'clicks');
});

// impressions
const totalGoogleAdsImpressions = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'impressions');
});

const totalGoogleAdsImpressionsChart = createSelector(
  [getNewStatsService('google-ads')],
  (stats) => {
    return simpleChart(stats, 'impressions');
  }
);

// CV
const googleConversionValue = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'conversionValue');
});

const ga_transactionRevenue_adGroup = googleConversionValue;

const googleConversionValueChart = createSelector([getNewStatsService('google-ads')], (stats) => {
  return simpleChart(stats, 'conversionValue');
});

// all CV
const googleAllConversionValue = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'allConversionValue');
});

const ga_all_transactionsRevenue_adGroup = googleAllConversionValue;

const googleAllConversionValueChart = createSelector(
  [getNewStatsService('google-ads')],
  (stats) => {
    return simpleChart(stats, 'allConversionValue');
  }
);

// all purchases (all conversions)
const ga_transactions_adGroup = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'allConversions');
});

const googleAdsAllConversionsChart = createSelector([getNewStatsService('google-ads')], (stats) => {
  return simpleChart(stats, 'allConversions');
});

// purchases (conversions)
const ga_all_transactions_adGroup = createSelector([getNewStatsService('google-ads')], (stats) => {
  return total(stats, 'purchases');
});

const googleAdsConversionsChart = createSelector([getNewStatsService('google-ads')], (stats) => {
  return simpleChart(stats, 'purchases');
});

const ga_ROAS = createSelector(
  [googleConversionValue, totalGoogleAdsCost],
  (conversionValue, spend) => {
    return safeDivide(conversionValue, spend);
  }
);

const googleAdsRoasChart = createSelector(
  [googleConversionValueChart, chartGoogleAds],
  (conversionValue, spend) => simpleChart2(conversionValue, spend)
);

const googleCpa = createSelector(
  [ga_transactions_adGroup, totalGoogleAdsCost],
  (purchases, spend) => {
    return safeDivide(spend, purchases);
  }
);

const googleCpaChart = createSelector(
  [googleAdsAllConversionsChart, chartGoogleAds],
  (purchases, spend) => simpleChart2(spend, purchases)
);

const googleAllCpa = createSelector(
  [ga_all_transactions_adGroup, totalGoogleAdsCost],
  (allPurchases, spend) => {
    return safeDivide(spend, allPurchases);
  }
);

const googleAllCpaChart = createSelector(
  [googleAdsConversionsChart, chartGoogleAds],
  (allPurchases, spend) => simpleChart2(spend, allPurchases)
);

const totalGoogleAdsCtr = createSelector(
  [totalGoogleAdsClicks, totalGoogleAdsImpressions],
  (clicks, impressions) => safeDivide(clicks, impressions) * 100
);

const totalGoogleAdsCtrChart = createSelector(
  [googleAdsClicksChart, totalGoogleAdsImpressionsChart],
  (clicks, impressions) => simpleChart2(clicks, impressions, { chart1yMultiplier: 100 })
);

const totalGoogleAdsCpm = createSelector(
  [totalGoogleAdsCost, totalGoogleAdsImpressions],
  (spend, impressions) => safeDivide(spend, impressions / 1000)
);

const totalGoogleAdsCpmChart = createSelector(
  [chartGoogleAds, totalGoogleAdsImpressionsChart],
  (spend, impressions) => simpleChart2(spend, impressions, { chart1yMultiplier: 1000 })
);

const googleCpc = createSelector([totalGoogleAdsClicks, totalGoogleAdsCost], (clicks, spend) =>
  safeDivide(spend, clicks)
);

const googleCpcChart = createSelector([chartGoogleAds, googleAdsClicksChart], (spend, clicks) =>
  simpleChart2(spend, clicks)
);

const totalAllRoas = createSelector(
  [ga_all_transactionsRevenue_adGroup, totalGoogleAdsCost],
  (ga_all_transactionsRevenue_adGroup, totalGoogleAdsCost) => {
    if (!totalGoogleAdsCost) return 0;
    return ga_all_transactionsRevenue_adGroup / totalGoogleAdsCost;
  }
);

const totalAllRoasChart = createSelector(
  [googleAllConversionValueChart, chartGoogleAds],
  (allCVChart, spendChart) => simpleChart2(allCVChart, spendChart)
);

// deprecated
const chartGoogleRoas = createSelector([getNewStatsService('google-ads')], (stats) => {
  var chart = [];
  var lastYearDays;
  var firstStatDay;
  var isOneDay = stats.length == 1;
  if (stats[0]) {
    lastYearDays = moment(stats[0].start).endOf('year').dayOfYear();
    firstStatDay = moment(stats[0].start).dayOfYear();
  }
  stats.forEach((day) => {
    if (isOneDay) {
      chart = Array.from({ length: 24 }).map((_, i) => ({
        x: Number(i),
        y: 0,
      }));
      Object.entries(day.hours).forEach(([key, hour]) => {
        var ga_adCost = hour['ga_adCost'];
        var ga_transactionRevenue_adGroup = hour['ga_transactionRevenue_adGroup'];
        var y;
        if (!ga_adCost) y = 0;
        else y = ga_transactionRevenue_adGroup / ga_adCost;
        chart.find((i) => i.x == Number(key)).y = y;
      });
    } else {
      let x = moment(day.start).dayOfYear();
      if (x < firstStatDay) x += lastYearDays;
      var y = 0;
      Object.values(day.hours).forEach((hour) => {
        var ga_adCost = hour['ga_adCost'];
        var ga_transactionRevenue_adGroup = hour['ga_transactionRevenue_adGroup'];
        if (!ga_adCost) y = 0;
        else y = ga_transactionRevenue_adGroup / ga_adCost;
      });

      chart.push({ x, y });
    }
  });

  return chart;
});

const PROPS_TO_SELECTORS = {};

export default {
  PROPS_TO_SELECTORS,
  SELECTORS: {
    totalGoogleAdsCost,
    chartGoogleAds,
    totalGoogleAdsClicks,
    googleAdsClicksChart,
    totalGoogleAdsImpressions,
    totalGoogleAdsImpressionsChart,
    googleConversionValue,
    ga_transactionRevenue_adGroup,
    googleConversionValueChart,
    googleAllConversionValue,
    ga_all_transactionsRevenue_adGroup,
    googleAllConversionValueChart,
    ga_transactions_adGroup,
    ga_all_transactions_adGroup,
    googleAdsConversionsChart,
    googleAdsAllConversionsChart,
    ga_ROAS,
    googleAdsRoasChart,
    googleCpa,
    googleCpaChart,
    googleAllCpa,
    googleAllCpaChart,
    totalGoogleAdsCtr,
    totalGoogleAdsCtrChart,
    totalGoogleAdsCpm,
    totalGoogleAdsCpmChart,
    googleCpc,
    googleCpcChart,
    googleRoas: ga_ROAS,
    ga_adCost: totalGoogleAdsCost,
    totalAllRoas,
    totalAllRoasChart,
    // deprecated
    chartGoogleRoas,
  },
};
